// __PLANNING__ (2026-10-26) the Planning workspace: the open hauling jobs (and the Trucking board's Trello-only cards,
// read-only) sorted into Trello-style lists by rule - Into / From GTA, London, Bradford, Wingham, Barrie, Tillsonburg,
// Northern / Southern locals, North / South of main lines - with drag and drop between lists, "also matches", and
// "goes well together" hints. The rules run here (utils/planningModel.js); the lists, rules, main lines and the cards'
// placements are the hub's (services/hubPlanning.js), shared by the office. Jobs' places come from the geodata the hub
// keeps on each job (billing_data.geo).
// __PLANNING_ASSIGN__ (P2 / P3) each job card has ASSIGN (a small driver picker); an assigned card is hatched, and
// Assign again reassigns it. "Drivers" shows Trello-style driver lists (drop a card on a driver to assign it). A
// reassignment re-reads the job first (reassignCheck): an assigned / en-route job warns that the old driver's route must
// be reconfirmed in Route Builder (the hub raises a Needs-attention alert that opens it, and tells a driver on the way);
// a job already picked up stays with its driver unless dispatch overrides it with a reason (audited on the hub).
// __JOB_ORDER__ (2026-10-27) "Drivers" lists each driver's jobs in his job order (the order of his Trello list and the
// Driver app); drag a card up or down in a driver's list to reorder it (the hub moves the Trello cards to match), or
// drop it at a place in another driver's list to assign it there.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, closestCorners, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../supabaseClient';
import { getPlanning, saveSettings, resetSettings, placeCard, reportAssigned, getJobOrder, saveJobOrder } from '../services/hubPlanning';
import { withDriverOrder, moveId, insertId } from '../utils/jobOrder';
import { jobItem, trelloItem, arrange, combos, face, visible, dropPos, UNSORTED, reassignCheck, driverColumns, applyNextDay } from '../utils/planningModel';
import PlanningRules from './planning/PlanningRules';
import { DriverPicker, ReassignDialog } from './planning/AssignControls';
import { useEquipmentGate } from './EquipmentGate';
import { fetchFleet, fetchRules } from '../services/equipmentService';   // __OPEN_DECK__ the ASSIGN picker's marks
import { truckRequiredHint, truckRequiredOf, TRUCK_REQUIRED } from '../utils/equipmentCompat';
import { reconcileRoutesAfterReassignment } from '../services/routingEngineService';

const PLAN_SELECT = `
  id, load_number, status, driver_id, customer_name, scheduled_date, started_at, billing_data,
  load_stops (id, stop_sequence, stop_type, address, latitude, longitude, city, province, place_name, completed),
  customer:customers(company_name)
`;
const OPEN = ['pending', 'assigned', 'en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading', 'pending_completion'];
const PREF = 'hmgc_plan_filters';
const readPref = () => { try { return JSON.parse(localStorage.getItem(PREF) || '{}') || {}; } catch (e) { return {}; } };

const chip = (bg) => ({ display: 'inline-block', padding: '0 0.4rem', borderRadius: '9px', fontSize: '0.66rem', lineHeight: '1.15rem', background: bg || 'var(--surface-3)', color: 'var(--text-primary)', whiteSpace: 'nowrap' });
const tbBtn = (on) => ({ padding: '0.35rem 0.75rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-primary)',
  background: on ? 'var(--btn-primary-bg)' : 'var(--btn-secondary-bg)', border: on ? 'none' : '1px solid var(--border-default)' });

const HATCH = 'repeating-linear-gradient(135deg, rgba(240,246,252,0.07) 0 7px, transparent 7px 14px)';
export const openRouteBuilder = () => { try { window.dispatchEvent(new CustomEvent('hb-open-view', { detail: { view: 'routes' } })); } catch (e) { /* old browser */ } };

export function PlanCard({ entry, color, listName, names, comboList, onCombo, highlighted, overlay, dragging, onUnpin, extra, driverName, onAssign }) {
  const { item, match, pinned } = entry;
  const f = face(item);
  const also = (match.also || []).map((id) => names[id]).filter(Boolean);
  const firstName = match.first ? names[match.first] : null;
  return (
    <div data-plan-card={item.key} title={f.tip} style={{
      position: 'relative', backgroundColor: 'var(--surface-1)', backgroundImage: item.driverId ? HATCH : 'none', borderRadius: 'var(--radius-md)', padding: '0.5rem 0.6rem',
      borderLeft: '4px solid ' + color, outline: highlighted ? '2px solid var(--accent-blue)' : '1px solid var(--border-default)', outlineOffset: '-1px',
      opacity: dragging ? 0.35 : 1, cursor: item.readOnly ? 'default' : (overlay ? 'grabbing' : 'grab'), userSelect: 'none',
      boxShadow: overlay ? 'var(--shadow-lg)' : 'none', transform: overlay ? 'rotate(1.5deg)' : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <span style={{ fontWeight: 700, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{f.title}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
          {item.kind === 'trello' && <a href={item.url || undefined} target="_blank" rel="noopener noreferrer" style={chip('#0079bf')} title={'Trello only (read-only) - list: ' + item.list} onPointerDown={(e) => e.stopPropagation()}>Trello</a>}
          {pinned && <button title={'Placed here by hand' + (firstName ? ' - its rule says ' + firstName : '') + '. Click to put it back.'} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onUnpin && onUnpin(item); }}
            style={{ ...chip('var(--surface-3)'), border: 'none', cursor: 'pointer' }}>📌</button>}
        </span>
      </div>
      <div style={{ fontSize: '0.82rem', lineHeight: 1.3, margin: '0.15rem 0', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{f.route}</div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{f.meta}{!item.hasGeo && item.kind === 'job' ? ' · places from the stops' : ''}</div>
      {(f.tags.length > 0 || (comboList && comboList.length > 0)) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.3rem' }}>
          {f.tags.map((t) => <span key={t} style={chip()}>{t}</span>)}
          {comboList && comboList.length > 0 && (
            <button data-plan-combo onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onCombo && onCombo(item, comboList); }}
              title={'Goes well together:\n' + comboList.slice(0, 8).map((c) => c.kind + ' (' + c.km + ' km): ' + (c.label || c.key)).join('\n')}
              style={{ ...chip('rgba(88,166,255,0.25)'), border: 'none', cursor: 'pointer' }}>🔗 {comboList.length} {comboList[0].kind}</button>
          )}
        </div>
      )}
      {also.length > 0 && <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Also matches: {also.join(', ')}</div>}
      {listName && pinned && firstName !== listName && <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Rule: {firstName || 'none'}</div>}
      {item.kind === 'job' && onAssign && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.35rem' }}>
          {item.driverId && <span data-plan-driver-chip style={{ ...chip('rgba(63,185,80,0.35)'), maxWidth: '9.5rem', overflow: 'hidden', textOverflow: 'ellipsis' }} title={'Assigned to ' + (driverName || 'a driver') + (item.status ? ' (' + item.status.replace(/_/g, ' ') + ')' : '')}>{driverName || 'Assigned'}</span>}
          <button data-plan-assign onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onAssign(item, e); }}
            style={{ marginLeft: 'auto', padding: '0.12rem 0.5rem', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.03em', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              color: 'var(--text-primary)', border: item.driverId ? '1px solid var(--border-default)' : 'none', background: item.driverId ? 'var(--btn-secondary-bg)' : 'var(--btn-primary-bg)' }}>
            {item.driverId ? 'Assign again' : 'ASSIGN'}</button>
        </div>
      )}
      {extra}
    </div>
  );
}

function SortableCard(props) {
  const { entry } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.item.key, disabled: !!entry.item.readOnly });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} {...(entry.item.readOnly ? {} : { ...attributes, ...listeners })}>
      <PlanCard {...props} dragging={isDragging} />
    </div>
  );
}

function Column({ col, names, comboMap, labelOf, onCombo, highlight, onUnpin, renderExtra, driverNameOf, onAssign }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'col:' + col.list.id });
  const ids = col.cards.map((c) => c.item.key);
  const jobs = col.cards.filter((c) => c.item.kind === 'job').length;
  return (
    <div data-plan-col={col.list.id} style={{ flex: '0 0 272px', display: 'flex', flexDirection: 'column', maxHeight: '100%', background: 'var(--surface-2)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', borderTop: '3px solid ' + col.list.color }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.65rem', fontWeight: 600, fontSize: '0.85rem' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.list.name}</span>
        <span style={{ marginLeft: 'auto', ...chip() }} title={jobs + ' job(s)' + (col.cards.length - jobs ? ', ' + (col.cards.length - jobs) + ' Trello card(s)' : '')}>{col.cards.length}</span>
      </div>
      <div ref={setNodeRef} style={{ flex: 1, overflowY: 'auto', padding: '0 0.5rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', minHeight: '3rem', background: isOver ? 'rgba(88,166,255,0.08)' : 'transparent', borderRadius: 'var(--radius-md)' }}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {col.cards.map((c) => (
            <SortableCard key={c.item.key} entry={c} color={col.list.color} listName={col.list.name} names={names}
              comboList={(comboMap[c.item.key] || []).map((x) => ({ ...x, label: labelOf(x.key) }))} onCombo={onCombo}
              highlighted={highlight.has(c.item.key)} onUnpin={onUnpin} extra={renderExtra ? renderExtra(c) : null}
              driverName={driverNameOf ? driverNameOf(c.item.driverId) : null} onAssign={onAssign} />
          ))}
        </SortableContext>
        {col.cards.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', padding: '0.8rem 0' }}>{col.list.id === UNSORTED ? 'Every job has a list.' : String(col.list.id).startsWith('drv:') ? 'No open jobs.' : 'No jobs match.'}</div>}
      </div>
    </div>
  );
}

export default function PlanningBoard({ renderExtra, itemFilter, headerExtra, onLoads }) {
  const [loads, setLoads] = useState([]);
  const [hub, setHub] = useState(null);
  const [hubErr, setHubErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [placements, setPlacements] = useState({});
  const [pref, setPref] = useState(readPref);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [highlight, setHighlight] = useState(new Set());
  const [status, setStatus] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [picker, setPicker] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [gateEquipment, equipmentModal] = useEquipmentGate();
  const timer = useRef(null);
  const [jobOrder, setJobOrder] = useState(null);   // __JOB_ORDER__ the hub's per-driver order
  const fetchOrder = useCallback(async () => {
    const r = await getJobOrder();
    if (r.ok) setJobOrder({ drivers: r.drivers || {}, routes: r.routes || {} });
  }, []);
  useEffect(() => {
    fetchOrder();
    const iv = setInterval(fetchOrder, 15000);   // a reorder on Trello shows here within ~30 s
    return () => clearInterval(iv);
  }, [fetchOrder]);

  const setP = (k, v) => setPref((p) => { const n = { ...p, [k]: v }; try { localStorage.setItem(PREF, JSON.stringify(n)); } catch (e) { /* a convenience */ } return n; });

  const fetchLoads = useCallback(async () => {
    const PAGE = 1000; const all = []; let from = 0;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data, error } = await supabase.from('loads').select(PLAN_SELECT).in('status', OPEN).order('created_at', { ascending: false }).range(from, from + PAGE - 1);
        if (error) { console.error('[planning] loads', error); break; }
        all.push(...(data || []));
        if (!data || data.length < PAGE) break;
        from += PAGE;
      }
      setLoads(all);
      if (onLoads) onLoads(all);
    } finally { setLoading(false); }
  }, [onLoads]);

  const fetchHub = useCallback(async (fresh) => {
    const r = await getPlanning(fresh);
    if (r.ok) { setHub(r); setPlacements(r.placements || {}); setHubErr(''); } else setHubErr(r.message || 'The planning lists could not be read.');
  }, []);

  useEffect(() => {
    supabase.from('drivers').select('id, name, is_available').then(({ data }) => setDrivers(data || []));
  }, []);
  // __OPEN_DECK__ (2026-10-27) the fleet + rules for the ASSIGN picker: a driver whose unit does not meet the job's Truck
  // required (an open-deck job on a tarp / curtain-side truck, a tarp job on an open deck) is marked before the pick
  const [equip, setEquip] = useState(null);
  useEffect(() => {
    let off = false;
    Promise.all([fetchFleet(), fetchRules()]).then(([fleet, rules]) => { if (!off) setEquip({ fleet, rules }); }).catch(() => {});
    return () => { off = true; };
  }, []);
  const loadById = useMemo(() => { const m = new Map(); loads.forEach((l) => m.set(l.id, l)); return m; }, [loads]);
  const truckHintFor = (item) => (driverId) => (equip && item && item.kind === 'job'
    ? truckRequiredHint({ driverId, row: loadById.get(item.id), fleet: equip.fleet, rules: equip.rules, day: item.date || undefined }) : '');
  const truckOf = (item) => (item && item.kind === 'job' ? TRUCK_REQUIRED[truckRequiredOf(loadById.get(item.id))] || '' : '');

  useEffect(() => {
    fetchLoads(); fetchHub(false);
    const ch = supabase.channel('planning-loads').on('postgres_changes', { event: '*', schema: 'public', table: 'loads' }, () => {
      clearTimeout(timer.current); timer.current = setTimeout(fetchLoads, 1500);
    }).subscribe();
    const iv = setInterval(() => fetchHub(false), 120000);
    return () => { supabase.removeChannel(ch); clearInterval(iv); clearTimeout(timer.current); };
  }, [fetchLoads, fetchHub]);

  const settings = hub && hub.settings;
  const areas = useMemo(() => (hub && hub.areas) || [], [hub]);
  const items = useMemo(() => {
    const j = applyNextDay(loads.map((l) => jobItem(l, areas)), hub && hub.next_day);   // __SPECIAL_CARDS__ below the Tomorrow divider
    const t = ((hub && hub.trello && hub.trello.items) || []).map((c) => trelloItem(c, areas));
    return [...j, ...t];
  }, [loads, hub, areas]);
  const shown = useMemo(() => items.filter((it) => visible(it, { q: pref.q, date: pref.date, undated: true, hideTrello: pref.hideTrello }) && (!itemFilter || itemFilter(it))), [items, pref, itemFilter]);
  const byDriver = pref.mode === 'drivers';
  const board = useMemo(() => (byDriver ? { columns: driverColumns(shown, drivers, jobOrder) } : settings ? arrange(shown, settings, placements) : { columns: [] }), [byDriver, shown, drivers, settings, placements, jobOrder]);
  const driverNames = useMemo(() => { const m = {}; drivers.forEach((d) => { m[d.id] = d.name || 'Driver'; }); return m; }, [drivers]);
  const driverNameOf = useCallback((id) => (id ? (driverNames[id] || 'a driver') : ''), [driverNames]);
  const comboMap = useMemo(() => (settings ? combos(shown.filter((i) => !i.driverId), settings.combo) : {}), [shown, settings]);
  const names = useMemo(() => { const m = {}; ((settings && settings.lists) || []).forEach((l) => { m[l.id] = l.name; }); return m; }, [settings]);
  const byKey = useMemo(() => { const m = {}; items.forEach((i) => { m[i.key] = i; }); return m; }, [items]);
  const labelOf = useCallback((k) => { const it = byKey[k]; if (!it) return k; const f = face(it); return f.title + ': ' + f.route; }, [byKey]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const colOf = (key) => board.columns.find((c) => c.cards.some((x) => x.item.key === key));

  const place = async (key, listId, pos, msg) => {
    const before = placements;
    const next = { ...placements };
    if (listId) next[key] = { list: listId, pos }; else delete next[key];
    setPlacements(next);
    const r = await placeCard(key, listId, pos);
    if (!r.ok) { setPlacements(before); setStatus({ type: 'error', text: 'Not moved: ' + r.message }); } else if (msg) setStatus({ type: 'ok', text: msg });
  };

  const onDragEnd = (ev) => {
    setActiveKey(null);
    const { active, over } = ev;
    if (!over) return;
    const key = String(active.id);
    const from = colOf(key);
    let to; let index;
    const oid = String(over.id);
    if (oid.startsWith('col:')) { to = board.columns.find((c) => c.list.id === oid.slice(4)); index = to ? to.cards.length : 0; }
    else { to = colOf(oid); index = to ? to.cards.findIndex((x) => x.item.key === oid) : 0; }
    if (!to || !from) return;
    if (byDriver) {   // a card dropped on a driver's list = assign it to that driver (at that place in his order)
      const it = byKey[key];
      if (!it || it.kind !== 'job') return;
      if (to !== from) {
        assignTo(it, to.driverId || null, to.driverId ? insertId(to.cards.map((x) => String(x.item.id)), String(it.id), index) : null);
        return;
      }
      // __JOB_ORDER__ up / down within one driver's list = his new job order (Trello and the Driver app follow)
      if (!to.driverId) return;
      const ids = to.cards.map((x) => String(x.item.id));
      const next = oid.startsWith('col:') ? moveId(ids, String(it.id), null) : moveId(ids, String(it.id), String((byKey[oid] || {}).id || ''));
      if (next.join() === ids.join()) return;
      reorderDriver(to.driverId, next, face(it).title + ' moved in ' + driverNameOf(to.driverId) + "'s list.");
      return;
    }
    const rest = to.cards.filter((x) => x.item.key !== key);
    if (from === to) { const cur = from.cards.findIndex((x) => x.item.key === key); if (cur === index) return; }
    const pos = dropPos(rest, Math.max(0, Math.min(index, rest.length)));
    const it = byKey[key];
    place(key, to.list.id, pos, from !== to ? (face(it).title + ' moved to ' + to.list.name + '.') : null);
  };

  // ---- __JOB_ORDER__ ----
  const reorderDriver = async (driverId, ids, msg) => {
    const before = jobOrder;
    setJobOrder((o) => withDriverOrder(o, driverId, ids));
    const r = await saveJobOrder(driverId, ids, 'Planning: Drivers');
    if (!r.ok) { setJobOrder(before); setStatus({ type: 'error', text: 'Order not saved: ' + r.message }); return; }
    setStatus({ type: 'ok', text: (msg || 'New order saved.') + (r.trello === 'moving' ? ' The Trello cards follow in a few seconds;' : '')
      + ' the Driver app shows the new order on its next refresh.' });
    fetchOrder();
  };

  // ---- __PLANNING_ASSIGN__ ----
  const onAssign = (item, ev) => {
    const r = ev && ev.currentTarget ? ev.currentTarget.getBoundingClientRect() : { left: 200, bottom: 200 };
    setPicker({ item, anchor: { x: r.left - 120, y: r.bottom + 4 } });
  };
  const countsFor = (date) => { const c = {}; loads.forEach((l) => { if (l.driver_id && (!date || String(l.scheduled_date || '').slice(0, 10) === date)) c[l.driver_id] = (c[l.driver_id] || 0) + 1; }); return c; };
  const assignTo = async (item, toId, orderIds) => {
    setPicker(null);
    const { data: live, error } = await supabase.from('loads').select('id, status, started_at, driver_id, scheduled_date, load_number, load_stops(stop_type, completed)').eq('id', item.id).single();
    if (error || !live) { setStatus({ type: 'error', text: 'Could not read the job: ' + ((error && error.message) || 'not found') }); return; }
    const fromId = live.driver_id || null;
    if (fromId === (toId || null)) { setStatus({ type: 'info', text: face(item).title + ' is already ' + (toId ? 'with ' + driverNameOf(toId) : 'unassigned') + '.' }); return; }
    const check = reassignCheck(live);
    let decision = { reason: '', override: false };
    if (check.tier !== 'ok') {
      decision = await new Promise((resolve) => setDialog({ item, check, fromId, toId, resolve }));
      setDialog(null);
      if (!decision || check.tier === 'blocked') return;
    }
    if (toId) {
      const eq = await gateEquipment({ driverId: toId, loadIds: [item.id], date: live.scheduled_date || undefined, context: 'planning_assign', title: 'Assign to ' + driverNameOf(toId) });
      if (!eq.proceed) return;
    }
    const upd = toId ? { driver_id: toId, status: 'assigned' } : { driver_id: null, status: 'pending' };
    const { error: e2 } = await supabase.from('loads').update(upd).eq('id', item.id);
    if (e2) { setStatus({ type: 'error', text: 'Not assigned: ' + e2.message }); return; }
    await reconcileRoutesAfterReassignment({ loadId: item.id, oldDriverId: fromId, newDriverId: toId || null, scheduledDate: live.scheduled_date || null });
    const rep = await reportAssigned({ load_id: item.id, from_driver_id: fromId, to_driver_id: toId || null, was_status: live.status, tier: check.tier,
      reason: decision.reason || '', override: !!decision.override });
    const who = toId ? driverNameOf(toId) : 'nobody';
    const text = face(item).title + (toId ? ' assigned to ' + who : ' unassigned') + (fromId ? ' (was ' + driverNameOf(fromId) + '). Reconfirm ' + driverNameOf(fromId) + "'s route in Route Builder" + (rep.ok ? ' - it is in the Alerts as Needs attention' : '') + '.' : '.')
      + (rep.ok && rep.driver_told ? ' ' + driverNameOf(fromId) + ' was told on their phone.' : '') + (rep.ok ? '' : ' (The hub was not told: ' + rep.message + ')');
    setStatus({ type: rep.ok ? 'ok' : 'error', text, action: fromId ? { label: 'Open Route Builder', onClick: openRouteBuilder } : null });
    fetchLoads();
    // __JOB_ORDER__ dropped at a place in the new driver's list: that place in his order
    if (toId && orderIds && orderIds.length > 1) { saveJobOrder(toId, orderIds, 'Planning: Drivers').then(() => fetchOrder()); }
  };

  const onCombo = (item, list) => {
    const s = new Set([item.key, ...list.map((x) => x.key)]);
    setHighlight((h) => (h.size && h.has(item.key) ? new Set() : s));
    setStatus({ type: 'info', text: face(item).title + ' goes well with ' + list.length + ': ' + list.slice(0, 3).map((x) => x.kind + ' ' + labelOf(x.key)).join('; ') + (list.length > 3 ? '…' : '') });
  };

  const active = activeKey ? (board.columns.flatMap((c) => c.cards).find((x) => x.item.key === activeKey)) : null;
  const activeCol = activeKey ? colOf(activeKey) : null;
  const counts = { jobs: shown.filter((i) => i.kind === 'job').length, trello: shown.filter((i) => i.kind === 'trello').length, noGeo: shown.filter((i) => i.kind === 'job' && !i.hasGeo).length };

  return (
    <div data-planning style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 350px)', minHeight: '520px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.6rem' }}>
        <input placeholder="Search customer, town, address, load…" value={pref.q || ''} onChange={(e) => setP('q', e.target.value)}
          style={{ padding: '0.4rem 0.6rem', minWidth: '15rem', background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }} />
        <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Day <input type="date" value={pref.date || ''} onChange={(e) => setP('date', e.target.value)}
          style={{ background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '0.25rem' }} /></label>
        {pref.date && <button style={tbBtn(false)} onClick={() => setP('date', '')}>All days</button>}
        <span style={{ display: 'inline-flex' }}>
          <button data-plan-mode="areas" style={{ ...tbBtn(!byDriver), borderRadius: 'var(--radius-md) 0 0 var(--radius-md)' }} onClick={() => setP('mode', 'areas')}>Areas</button>
          <button data-plan-mode="drivers" style={{ ...tbBtn(byDriver), borderRadius: '0 var(--radius-md) var(--radius-md) 0' }} onClick={() => setP('mode', 'drivers')} title="One list per driver - drop a card on a driver to assign it">Drivers</button>
        </span>
        {!byDriver && <button style={tbBtn(!pref.hideTrello)} onClick={() => setP('hideTrello', !pref.hideTrello)} title="The Trucking board's cards that have no hauling job (read-only)">Trello cards</button>}
        {headerExtra}
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{counts.jobs} jobs{counts.trello ? ' · ' + counts.trello + ' Trello' : ''}{counts.noGeo ? ' · ' + counts.noGeo + ' without geodata yet' : ''}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <button style={tbBtn(false)} onClick={() => { fetchLoads(); fetchHub(true); }}>Refresh</button>
          <button data-plan-rules-btn style={tbBtn(false)} onClick={() => setRulesOpen(true)} disabled={!settings}>⚙ Lists &amp; rules</button>
        </span>
      </div>
      {(status || hubErr || (hub && hub.trello && hub.trello.note)) && (
        <div data-plan-status style={{ fontSize: '0.8rem', marginBottom: '0.5rem', color: hubErr || (status && status.type === 'error') ? 'var(--color-danger-emphasis)' : 'var(--text-secondary)' }}>
          {hubErr ? 'Planning lists: ' + hubErr : status ? status.text : hub.trello.note}
          {status && status.action && <button data-plan-status-action style={{ ...tbBtn(false), marginLeft: '0.5rem', padding: '0.15rem 0.55rem' }} onClick={status.action.onClick}>{status.action.label}</button>}
          {status && <button style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }} onClick={() => { setStatus(null); setHighlight(new Set()); }}>×</button>}
        </div>
      )}
      {loading && !loads.length ? <div style={{ color: 'var(--text-muted)' }}>Loading the open jobs…</div> : (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={(e) => setActiveKey(String(e.active.id))} onDragCancel={() => setActiveKey(null)} onDragEnd={onDragEnd}>
          <div style={{ flex: 1, display: 'flex', gap: '0.6rem', overflowX: 'auto', overflowY: 'hidden', paddingBottom: '0.4rem' }}>
            {board.columns.map((col) => (
              <Column key={col.list.id} col={col} names={names} comboMap={comboMap} labelOf={labelOf} onCombo={onCombo} highlight={highlight}
                onUnpin={(it) => place(it.key, null, 0, face(it).title + ' is back in the list its rule picks.')} renderExtra={renderExtra}
                driverNameOf={driverNameOf} onAssign={onAssign} />
            ))}
          </div>
          <DragOverlay>{active ? <PlanCard entry={active} color={activeCol ? activeCol.list.color : '#8b949e'} names={names} overlay driverName={driverNameOf(active.item.driverId)} /> : null}</DragOverlay>
        </DndContext>
      )}
      {picker && <DriverPicker anchor={picker.anchor} drivers={drivers} counts={countsFor(picker.item.date)} current={picker.item.driverId} date={picker.item.date}
        hintFor={truckHintFor(picker.item)} truckLabel={truckOf(picker.item)}
        onPick={(id) => assignTo(picker.item, id)} onClose={() => setPicker(null)} />}
      {dialog && <ReassignDialog title={(dialog.toId ? 'Reassign ' : 'Unassign ') + face(dialog.item).title} check={dialog.check}
        fromName={dialog.fromId ? driverNameOf(dialog.fromId) : ''} toName={dialog.toId ? driverNameOf(dialog.toId) : ''}
        onConfirm={async (d) => dialog.resolve(d)} onCancel={() => dialog.resolve(null)} />}
      {equipmentModal}
      {rulesOpen && settings && (
        <PlanningRules settings={settings} areas={areas} items={items} canEdit={!!hub.can_edit_rules}
          onClose={() => setRulesOpen(false)}
          onSave={async (d) => { const r = await saveSettings(d); if (r.ok) { setHub({ ...hub, settings: r.settings }); setRulesOpen(false); setStatus({ type: 'ok', text: 'Lists and rules saved.' }); } else window.alert('Not saved: ' + r.message); }}
          onReset={async () => { const r = await resetSettings(); if (r.ok) { setHub({ ...hub, settings: r.settings }); setRulesOpen(false); setStatus({ type: 'ok', text: 'The default lists and rules are back.' }); } else window.alert('Not reset: ' + r.message); }} />
      )}
    </div>
  );
}
